'use client';

import { c as _c } from "react/compiler-runtime";
import { fixUsersFromSharepoint, lookupSharepointUsers, type FixUsersFromSharepointResult, type LookupSharepointUsersResult, type SharepointUserRow } from '@/app/_workflows/fixUsersFromSharepoint';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
interface FixUsersFromSharepointModalProps {
  isOpen: boolean;
  onClose(): void;
}

// Column names of the SharePoint users export.
const NAME_COL = 'Name';
const EMAIL_COL = 'Email';
const LOCATION_COL = 'Location';
const STATUS_COL = 'Status';
const ID_COL = 'ID';
const CONTACT_ID_COL = 'SFContactID';
type Step = 'upload' | 'review' | 'done';
function parseFile(file: File): Promise<SharepointUserRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array'
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: null
        });
        const cell = (row: Record<string, unknown>, column: string): string => String(row[column] ?? '').trim();
        const parsed: SharepointUserRow[] = rows.map(row => ({
          name: cell(row, NAME_COL),
          email: cell(row, EMAIL_COL),
          location: cell(row, LOCATION_COL),
          status: cell(row, STATUS_COL),
          sfId: cell(row, ID_COL),
          contactId: cell(row, CONTACT_ID_COL)
        })).filter(r => r.email !== '' || r.name !== '');
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
const ErrorList: React.FC<{
  title: string;
  items: string[];
}> = t0 => {
  const $ = _c(11);
  const {
    title,
    items
  } = t0;
  if (items.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== items.length || $[1] !== title) {
    t1 = <Typography variant="subtitle2" color="error.main" gutterBottom={true}>{title} ({items.length})</Typography>;
    $[0] = items.length;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Divider sx={{
      mb: 1
    }} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== items) {
    t3 = items.map(_temp);
    $[4] = items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <List dense={true} disablePadding={true}>{t3}</List>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <Box>{t1}{t2}{t4}</Box>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const FixUsersFromSharepointModal: React.FC<FixUsersFromSharepointModalProps> = t0 => {
  const $ = _c(44);
  const {
    isOpen,
    onClose
  } = t0;
  const [step, setStep] = useState("upload");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rows, setRows] = useState(t1);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t2;
  if ($[1] !== isLoading || $[2] !== onClose) {
    t2 = () => {
      if (isLoading) {
        return;
      }
      setStep("upload");
      setRows([]);
      setFileName(null);
      setParseError(null);
      setLookup(null);
      setResult(null);
      onClose();
    };
    $[1] = isLoading;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClose = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async file => {
      setParseError(null);
      setRows([]);
      setFileName(file.name);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setParseError(`No rows found. The file needs the columns "${NAME_COL}", "${EMAIL_COL}", "${LOCATION_COL}", "${STATUS_COL}", "${ID_COL}" and "${CONTACT_ID_COL}".`);
          return;
        }
        setRows(parsed);
      } catch {
        setParseError("Failed to parse file. Make sure it is a valid Excel or CSV file.");
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const processFile = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      e.preventDefault();
      setIsDragOver(false);
      const file_0 = e.dataTransfer.files[0];
      if (file_0) {
        processFile(file_0);
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleDrop = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      const file_1 = e_0.target.files?.[0];
      if (file_1) {
        processFile(file_1);
      }
      e_0.target.value = "";
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleFileChange = t5;
  let t6;
  if ($[7] !== doServerAction || $[8] !== rows) {
    t6 = () => {
      doServerAction(async () => ({
        data: await lookupSharepointUsers(rows)
      }), response => {
        if (!response || typeof response !== "object" || !("data" in response)) {
          return;
        }
        setLookup(response.data);
        setStep("review");
      });
    };
    $[7] = doServerAction;
    $[8] = rows;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleLookup = t6;
  let t7;
  if ($[10] !== doServerAction || $[11] !== lookup) {
    t7 = () => {
      if (!lookup) {
        return;
      }
      doServerAction(async () => ({
        data: await fixUsersFromSharepoint(lookup.found, lookup.notFound)
      }), response_0 => {
        if (!response_0 || typeof response_0 !== "object" || !("data" in response_0)) {
          return;
        }
        setResult(response_0.data);
        setStep("done");
      });
    };
    $[10] = doServerAction;
    $[11] = lookup;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleConfirm = t7;
  let t8;
  if ($[13] !== handleClose || $[14] !== handleConfirm || $[15] !== handleLookup || $[16] !== isLoading || $[17] !== lookup || $[18] !== rows || $[19] !== step) {
    t8 = () => {
      if (step === "upload") {
        return <Button variant="contained" disabled={rows.length === 0 || isLoading} onClick={handleLookup}>Next {rows.length > 0 ? `(${rows.length} rows)` : ""}</Button>;
      }
      if (step === "review") {
        const total = lookup ? lookup.found.length + lookup.notFound.length : 0;
        return <Box sx={{
          display: "flex",
          gap: 1
        }}><Button variant="outlined" disabled={isLoading} onClick={() => setStep("upload")}>Back</Button><Button variant="contained" disabled={total === 0 || isLoading} onClick={handleConfirm}>{"Confirm & Save"}{lookup ? ` (${lookup.found.length} to update, ${lookup.notFound.length} to create)` : ""}</Button></Box>;
      }
      return <Button variant="contained" onClick={handleClose}>Close</Button>;
    };
    $[13] = handleClose;
    $[14] = handleConfirm;
    $[15] = handleLookup;
    $[16] = isLoading;
    $[17] = lookup;
    $[18] = rows;
    $[19] = step;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const renderActions = t8;
  let t9;
  if ($[21] !== renderActions) {
    t9 = renderActions();
    $[21] = renderActions;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {};
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== fileName || $[25] !== isDragOver || $[26] !== parseError || $[27] !== rows || $[28] !== step) {
    t11 = step === "upload" && <><Box onDrop={handleDrop} onDragOver={e_1 => {
        e_1.preventDefault();
        setIsDragOver(true);
      }} onDragLeave={() => setIsDragOver(false)} onClick={() => fileInputRef.current?.click()} sx={{
        mt: 1,
        mb: 2,
        border: 2,
        borderStyle: "dashed",
        borderColor: isDragOver ? "primary.main" : "divider",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        backgroundColor: isDragOver ? "action.hover" : "background.paper",
        transition: "all 0.15s ease"
      }}><CloudUploadIcon sx={{
          fontSize: 40,
          color: "text.secondary"
        }} /><Typography variant="body2" color="text.secondary">{"Drag & drop or click to select a file"}</Typography><Typography variant="caption" color="text.disabled">Supports .xlsx, .xls, .csv</Typography><input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{
          display: "none"
        }} onChange={handleFileChange} /></Box>{parseError && <Alert severity="error" sx={{
        mb: 2
      }}>{parseError}</Alert>}{rows.length > 0 && <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap"
      }}><Chip label={`${rows.length} rows ready`} color="success" size="small" />{fileName && <Typography variant="caption" color="text.secondary">{fileName}</Typography>}</Box>}</>;
    $[24] = fileName;
    $[25] = isDragOver;
    $[26] = parseError;
    $[27] = rows;
    $[28] = step;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== lookup || $[31] !== step) {
    t12 = step === "review" && lookup && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Alert severity={lookup.rejected.length > 0 ? "warning" : "success"}>{lookup.found.length} to update, {lookup.notFound.length} to create{lookup.rejected.length > 0 ? `, ${lookup.rejected.length} rejected` : ""}. Review and confirm to save.</Alert>{lookup.found.length > 0 && <Box><Typography variant="subtitle2" color="success.main" gutterBottom={true}>Update ({lookup.found.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.found.map(_temp2)}</List></Box>}{lookup.notFound.length > 0 && <Box><Typography variant="subtitle2" color="warning.main" gutterBottom={true}>Create ({lookup.notFound.length})</Typography><Typography variant="caption" color="text.secondary">New users get the USER role, JMT access off and QMT access on.</Typography><Divider sx={{
          my: 1
        }} /><List dense={true} disablePadding={true}>{lookup.notFound.map(_temp3)}</List></Box>}<ErrorList title="Rejected rows" items={lookup.rejected} /></Box>;
    $[30] = lookup;
    $[31] = step;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== result || $[34] !== step) {
    t13 = step === "done" && result && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><CheckCircleOutlineIcon color={result.errors.length ? "warning" : "success"} /><Typography variant="body1" fontWeight={500}>Done</Typography></Box><Alert severity={result.errors.length > 0 ? "warning" : "success"}>{result.updated} user{result.updated !== 1 ? "s" : ""} updated, {result.created} created.</Alert><ErrorList title="Errors" items={result.errors} /></Box>;
    $[33] = result;
    $[34] = step;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== handleClose || $[37] !== isLoading || $[38] !== isOpen || $[39] !== t11 || $[40] !== t12 || $[41] !== t13 || $[42] !== t9) {
    t14 = <Modal isOpen={isOpen} onClose={handleClose} title="Fix users according to SharePoint" subTitle="Upload the SharePoint users export. Users are matched by email. Name, site, active flag, salesforceId and contactId come from the file. Existing users keep their role and access." maxWidth="sm" fullWidth={true} disableClose={isLoading} isLoading={isLoading} actions={t9} ContentProps={t10}>{t11}{t12}{t13}</Modal>;
    $[36] = handleClose;
    $[37] = isLoading;
    $[38] = isOpen;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
    $[42] = t9;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
function _temp(item) {
  return <ListItem key={item} disableGutters={true}><ListItemText primary={item} primaryTypographyProps={{
      variant: "body2",
      color: "error"
    }} /></ListItem>;
}
function _temp2(u) {
  return <ListItem key={u.userId} disableGutters={true}><ListItemText primary={`${u.name || u.currentName} — ${u.location}, ${u.status}, salesforceId: ${u.sfId}${u.contactId ? `, contactId: ${u.contactId}` : ""}`} secondary={u.email} primaryTypographyProps={{
      variant: "body2"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: "text.secondary"
    }} /></ListItem>;
}
function _temp3(u_0) {
  return <ListItem key={u_0.email} disableGutters={true}><ListItemText primary={`${u_0.name || "(no name)"} — ${u_0.location}, ${u_0.status}, salesforceId: ${u_0.sfId}${u_0.contactId ? `, contactId: ${u_0.contactId}` : ""}`} secondary={u_0.email} primaryTypographyProps={{
      variant: "body2",
      color: "text.secondary"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: "text.disabled"
    }} /></ListItem>;
}